import React from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface ContentContainerProps {
    children: React.ReactNode;
    rightContainerIcon?: React.ReactNode;
    rightContainerText?: string;
    onRightContainerClick?: () => void;
    leftContainerIcon?: React.ReactNode;
    leftContainerText?: string;
    onLeftContainerClick?: () => void;
}

const ContentContainer: React.FC<ContentContainerProps> = ({
    children,
    rightContainerIcon,
    rightContainerText,
    onRightContainerClick,
    leftContainerIcon,
    leftContainerText,
    onLeftContainerClick,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const containerButtonStyles = {
        width: "100px",
        height: "100%",
        textAlign: "center",
        borderRadius: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--oc-footer-bg)",
        color: "var(--oc-text)",
        "&:hover": { backgroundColor: "var(--oc-paper-bg)" },
    };

    const wrapperBoxStyle = {
        width: "100px", // match button width
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
    };

    const footerPadding = 60;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                height: "100%",
                overflow: "hidden",
            }}
        >
            {/* Left wrapper (empty if no button, but still takes space) */}
            {!isMobile && (
                <Box sx={wrapperBoxStyle}>
                    {onLeftContainerClick && (
                        <Button
                            variant="contained"
                            sx={containerButtonStyles}
                            onClick={onLeftContainerClick}
                        >
                            {leftContainerIcon}
                            {leftContainerText && <span>{leftContainerText}</span>}
                        </Button>
                    )}
                </Box>
            )}

            {/* Scrollable Content */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    height: "100%",
                    paddingBottom: `${footerPadding}px`,
                    px: { xs: 2, md: 4 },
                }}
            >
                {children}
            </Box>

            {/* Right wrapper (empty if no button, but still takes space) */}
            {!isMobile && (
                <Box sx={wrapperBoxStyle}>
                    {onRightContainerClick && (
                        <Button
                            variant="contained"
                            sx={containerButtonStyles}
                            onClick={onRightContainerClick}
                        >
                            {rightContainerIcon}
                            {rightContainerText && <span>{rightContainerText}</span>}
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ContentContainer;
